<script>
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";

const editorSettings = {
    steps: {
        type: 'ARRAY',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'label',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                label: {
                    type: 'string',
                    label: 'Step Label',
                    id: 'label',
                    default: 'Label',
                    helpText: 'Label for the step.'
                },
                hideNumber: {
                    type: 'boolean',
                    label: 'Hide Number',
                    id: 'hideNumber',
                    helpText: 'Hide the number of the step.',
                    default: false
                },
                hideLabel: {
                    type: 'boolean',
                    label: 'Hide Label',
                    id: 'hideLabel',
                    helpText: 'Hide the label of the step.',
                    default: false
                },
                includeWhen: {
                    label: 'Include When',
                    id: 'includeWhen',
                    type: 'logicEditor',
                    helpText: 'Only include the step in the indicator when this logic criteria is met.',
                    default: [],
                    otherProps: {
                        hideStandardRecordTypes: true,
                        recordTypes: [
                            { text: 'Step Record', value: 'component.record' },
                            { text: 'Step', value: 'self' },
                        ],
                        defaultRecordType: 'self.record'
                    }
                },
                completeWhen: {
                    label: 'Complete When',
                    id: 'completeWhen',
                    type: 'logicEditor',
                    helpText: 'Mark the step "Complete" when this logic criteria is met.',
                    default: [],
                    otherProps: {
                        hideStandardRecordTypes: true,
                        recordTypes: [
                            { text: 'Step Record', value: 'component.record' },
                            { text: 'Step', value: 'self' },
                        ],
                        defaultRecordType: 'self.record'
                    }
                },
            },
            newItem: {
                label: 'Label',
                hideNumber: false,
                includeWhen: false,
                completeWhen: false,
                include: true,
                active: false,
                complete: false
            }
        }
    },
    indicatorSize: {
        label: 'Step Indicator Size',
        id: 'indicatorSize',
        type: 'dropdown',
        items: [
            { text: 'small', value: 'small-step-indicator' },
            { text: 'medium', value: 'medium-step-indicator' },
            { text: 'large', value: 'large-step-indicator' },
            { text: 'Extra Large', value: 'xlarge-step-indicator' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Set the size of the step indicator.',
        default: 'medium-step-indicator'
    },
    stepDividerType: {
        label: 'Step Divider',
        id: 'stepDividerType',
        type: 'dropdown',
        items: [
            'dash'
        ],
        helpText: 'Symbol that is used in between steps',
        default: 'dash'
    },
    inactiveBgColor: {
        label: 'Inactive Background Color',
        id: 'inactiveBgColor',
        type: 'color',
        helpText: 'Color of the number\'s outer shape when the step is inactive.',
        default: '#7c7c7cFF'
    },
    inactiveNumberColor: {
        label: 'Inactive Number Color',
        id: 'inactiveNumberColor',
        type: 'color',
        helpText: 'Color of the number text when the step is inactive.',
        default: '#f1f1f1FF'
    },
    inactiveLabelBgColor: {
        label: 'Inactive Label Background Color',
        id: 'inactiveLabelBgColor',
        type: 'color',
        helpText: 'Label background color when the step is inactive.',
        default: '#e0e0e0FF'
    },
    inactiveLabelTextColor: {
        label: 'Inactive Label Background Color',
        id: 'inactiveLabelTextColor',
        type: 'color',
        helpText: 'Label text color when the step is inactive.',
        default: '#555555FF'
    },
    activeBgColor: {
        label: 'Active Background Color',
        id: 'activeBgColor',
        type: 'color',
        helpText: 'Color of the number\'s outer shape when the step is active.',
        default: '#FFE50BFF'
    },
    activeNumberColor: {
        label: 'Active Number Color',
        id: 'activeNumberColor',
        type: 'color',
        helpText: 'Color of the number text when the step is active.',
        default: '#000000FF'
    },
    activeLabelBgColor: {
        label: 'Active Label Background Color',
        id: 'activeLabelBgColor',
        type: 'color',
        helpText: 'Label background color when the step is active.',
        default: '#fff69cFF'
    },
    activeLabelTextColor: {
        label: 'Active Label Text Color',
        id: 'activeLabelTextColor',
        type: 'color',
        helpText: 'Label text color when the step is active.',
        default: '#1d1903FF'
    },
    completeBgColor: {
        label: 'Complete Background Color',
        id: 'completeBgColor',
        type: 'color',
        helpText: 'Color of the number\'s outer shape when the step is complete.',
        default: '#0974bcFF'
    },
    completeNumberColor: {
        label: 'Complete Number Color',
        id: 'completeNumberColor',
        type: 'color',
        helpText: 'Color of the number text when the step is complete.',
        default: '#FFFFFFFF'
    },
    completeLabelBgColor: {
        label: 'Complete Label Background Color',
        id: 'completeLabelBgColor',
        type: 'color',
        helpText: 'Label background color when the step is complete.',
        default: '#a1cbf5FF'
    },
    completeLabelTextColor: {
        label: 'Complete Label Text Color',
        id: 'completeLabelTextColor',
        type: 'color',
        helpText: 'Label text color when the step is complete.',
        default: '#000000FF'
    },
};

const stepIndicatorConfig = {
    cId: "stepIndicator",
    cType: "stepIndicator",
    cTitle: "Step Indicator",
    cTemplate: `<div v-if="config && config.steps && config.steps.length" :class="config.indicatorSize">
        <div v-for="step in allSteps" class="step-item" :class="[{ 'step-active': step.active, 'step-complete': step.complete, 'hide-label': step.hideLabel, 'hide-number': step.hideNumber }]">
            <div class="step-inner">
                <div class="step-number">
                    <div class="step-circle" :style="{background: step.bgColor, color: step.numberColor}"><span>{{step.number}}</span></div>
                </div><div class="step-label" :style="{background: step.labelBgColor, color: step.labelTextColor}">{{step.label}}<span class="e-icons e-check"></span></div>
            </div>
        </div>
    </div>`
};

const stepIndicatorCmp = {
    name: "stepIndicator",
    editorSettings: editorSettings,
    props: {
        record: {
            type: Object,
            default: {}
        }
    },
    data() {
        return {
            record: false
        };
    },
    computed: {
        allSteps() {
            let currNumber = 1;
            let currActiveStep = false;
            let steps = library.cloneObj(this.config.steps);
            return steps.filter((step) => {
                step.include = true;
                library.logicParser.call(this, step.includeWhen, step);
                return step.include;
            }).map((step) => {
                // Set step number
                step.number = currNumber;
                currNumber++;
                // Set step number
                if (!step.complete && !currActiveStep) {
                    library.logicParser.call(this, step.completeWhen, step);
                    if (!step.complete) {
                        currActiveStep = true;
                        step.active = true;
                    }
                }
                // Set step label
                if (!step.label) step.hideLabel = true;
                // Set step colors
                let stepState = 'inactive';
                if (step.complete) stepState = 'complete';
                else if (step.active) stepState = 'active';
                step.bgColor = this.config[stepState + 'BgColor'];
                step.numberColor = this.config[stepState + 'NumberColor'];
                step.labelBgColor = this.config[stepState + 'LabelBgColor'];
                step.labelTextColor = this.config[stepState + 'LabelTextColor'];
                return step;
            });
        }
    }
};
componentStore.initComponent(stepIndicatorCmp, stepIndicatorConfig, '@common/stepIndicator.vue');

export default stepIndicatorCmp;
</script>

<style scoped>
    .step-item {
        --inactive-number-bgColor: #7c7c7c;
        --inactive-number-textColor: #f1f1f1;
        --inactive-label-bgColor: #e0e0e0;
        --inactive-label-textColor: #555;
        --active-number-bgColor: #FFE50B;
        --active-label-bgColor: #fff69c;
        --active-number-textColor: black;
        --active-label-textColor: #1d1903;
        --complete-number-bgColor: #0974bc;
        --complete-label-bgColor: #a1cbf5;
        --complete-number-textColor: #ffffff;
        --complete-label-textColor: black;
    }
    .step-item {
        display: inline-block;
        position: relative;
        z-index: 1;
    }
    .step-number {
        display: inline-block;
        position: relative;
        width: 28px;
        overflow: visible;
        font-weight: bold;
        color: var(--inactive-number-textColor);
        z-index: 1;
        font-size: 20px;
    }
    .step-item + .step-item .step-inner:before {
        content: " ";
        display: inline-block;
        width: 15px;
        height: 3px;
        margin: 0 10px;
        position: relative;
        top: -3px;
        border-radius: 2px;
        background: var(--inactive-number-bgColor);
    }
    .step-circle {
        display: flex;
        background: var(--inactive-number-bgColor);
        width: 40px;
        height: 40px;
        border-radius: 50%;
        align-items: center;
        justify-content: center;
    }
    .step-inner {
        display: inline-block;
    }
    .step-label {
        display: inline-block;
        padding: 7px 7px 7px 20px;
        border-radius: 0 5px 5px 0;
        background: var(--inactive-label-bgColor);
        color: var(--inactive-label-textColor);
        font-size: 18px;
    }
    .hide-number .step-number {
        display: none;
    }
    .hide-number .step-label {
        padding-left: 7px;
    }
    .hide-label .step-label {
        display: none;
    }
    .hide-label .step-number {
        width:40px;
    }
    /* Indicator Sizing */
    .step-item {
        --numberWidth: 20px;
        --numberFontSize: 16px;
        --dashWidth: 8px;
        --dashHeight: 2px;
        --dashMargin: 6px;
        --circleSize: 34px;
        --labelTBRPadding: 5px;
        --labelLPadding: 19px;
        --labelFontSize: 14px;
        --iconFontSize: 12px;
        --fontDif: 4px;
        --numberWidthDif: 6px;
        --circleSizeDif: 8px;
        --paddingDif: 1px;
        --paddingLeftDif: 4px;
        --dashWidthDif: 6px;
        --dashHeightDif: 1px;
        --dashMarginDif: 5px;
    }
    /* Small Step Indicator */
    .small-step-indicator .step-number {
        width: var(--numberWidth);
        font-size: var(--numberFontSize);
    }
    .small-step-indicator .step-item + .step-item .step-inner:before {
        width: var(--dashWidth);
        height: var(--dashHeight);
        margin-left: var(--dashMargin);
        margin-right: var(--dashMargin);
    }
    .small-step-indicator .step-circle {
        width: var(--circleSize);
        height: var(--circleSize);
    }
    .small-step-indicator .step-label {
        padding: var(--labelTBRPadding);
        padding-left: var(--labelLPadding);
        font-size: var(--labelFontSize);
    }
    .small-step-indicator .hide-number .step-label {
        padding-left: var(--labelTBRPadding);
    }
    .small-step-indicator .hide-label .step-number {
        width: var(--circleSize);
    }
    .small-step-indicator .e-check {
        font-size: var(--iconFontSize);
    }

    /* Medium Step Indicator */
    .medium-step-indicator .step-number {
        width: calc(var(--numberWidth) + var(--numberWidthDif));
        font-size: calc(var(--numberFontSize) + var(--fontDif));
    }
    .medium-step-indicator .step-item + .step-item .step-inner:before {
        width: calc(var(--dashWidth) + var(--dashWidthDif));
        height: calc(var(--dashHeight) + var(--dashHeightDif));
        margin-left: calc(var(--dashMargin) + var(--dashMarginDif));
        margin-right: calc(var(--dashMargin) + var(--dashMarginDif));
    }
    .medium-step-indicator .step-circle {
        width: calc(var(--circleSize) + var(--circleSizeDif));
        height: calc(var(--circleSize) + var(--circleSizeDif));
    }
    .medium-step-indicator .step-label {
        padding: calc(var(--labelTBRPadding) + var(--paddingDif));
        padding-left: calc(var(--labelLPadding) + var(--paddingLeftDif));
        font-size: calc(var(--labelFontSize) + var(--fontDif));
    }
    .medium-step-indicator .hide-number .step-label {
        padding-left: calc(var(--labelTBRPadding) + var(--numberWidthDif));
    }
    .medium-step-indicator .hide-label .step-number {
        width: calc(var(--circleSize) + var(--circleSizeDif));
    }
    .medium-step-indicator .e-check {
        font-size: calc(var(--iconFontSize) + var(--fontDif));
    }

    /* Large Step Indicator */
    .large-step-indicator .step-number {
        width: calc(var(--numberWidth) + (var(--numberWidthDif) * 2));
        font-size: calc(var(--numberFontSize) + (var(--fontDif) * 2));
    }
    .large-step-indicator .step-item + .step-item .step-inner:before {
        width: calc(var(--dashWidth) + (var(--dashWidthDif) * 2));
        height: calc(var(--dashHeight) + (var(--dashHeightDif) * 2));
        margin-left: calc(var(--dashMargin) + (var(--dashMarginDif) * 2));
        margin-right: calc(var(--dashMargin) + (var(--dashMarginDif) * 2));
    }
    .large-step-indicator .step-circle {
        width: calc(var(--circleSize) + (var(--circleSizeDif) * 2));
        height: calc(var(--circleSize) + (var(--circleSizeDif) * 2));
    }
    .large-step-indicator .step-label {
        padding: calc(var(--labelTBRPadding) + (var(--paddingDif) * 2));
        padding-left: calc(var(--labelLPadding) + (var(--paddingLeftDif) * 2));
        font-size: calc(var(--labelFontSize) + (var(--fontDif) * 2));
    }
    .large-step-indicator .hide-number .step-label {
        padding-left: calc(var(--labelTBRPadding) + (var(--numberWidthDif) * 2));
    }
    .large-step-indicator .hide-label .step-number {
        width: calc(var(--circleSize) + (var(--circleSizeDif) * 2));
    }
    .large-step-indicator .e-check {
        font-size: calc(var(--iconFontSize) + (var(--fontDif) * 2));
    }

    /* ExtraLarge Step Indicator */
    .xlarge-step-indicator .step-number {
        width: calc(var(--numberWidth) + (var(--numberWidthDif) * 3));
        font-size: calc(var(--numberFontSize) + (var(--fontDif) * 3));
    }
    .xlarge-step-indicator .step-item + .step-item .step-inner:before {
        width: calc(var(--dashWidth) + (var(--dashWidthDif) * 3));
        height: calc(var(--dashHeight) + (var(--dashHeightDif) * 3));
        margin-left: calc(var(--dashMargin) + (var(--dashMarginDif) * 3));
        margin-right: calc(var(--dashMargin) + (var(--dashMarginDif) * 3));
    }
    .xlarge-step-indicator .step-circle {
        width: calc(var(--circleSize) + (var(--circleSizeDif) * 3));
        height: calc(var(--circleSize) + (var(--circleSizeDif) * 3));
    }
    .xlarge-step-indicator .step-label {
        padding: calc(var(--labelTBRPadding) + (var(--paddingDif) * 3));
        padding-left: calc(var(--labelLPadding) + (var(--paddingLeftDif) * 3));
        font-size: calc(var(--labelFontSize) + (var(--fontDif) * 3));
    }
    .xlarge-step-indicator .hide-number .step-label {
        padding-left: calc(var(--labelTBRPadding) + (var(--numberWidthDif) * 3));
    }
    .xlarge-step-indicator .hide-label .step-number {
        width: calc(var(--circleSize) + (var(--circleSizeDif) * 3));
    }
    .xlarge-step-indicator .e-check {
        font-size: calc(var(--iconFontSize) + (var(--fontDif) * 3));
    }

    /* Step Active */
    .step-active .step-circle {
        background: var(--active-number-bgColor);
    }
    .step-active .step-number {
        color: var(--active-number-textColor);
    }
    .step-item + .step-item.step-active .step-inner:before {
        background: var(--active-number-bgColor);
    }
    .step-active .step-label {
        background: var(--active-label-bgColor);
        color: var(--active-label-textColor);
    }
    /* Step Complete */
    .step-complete .step-circle {
        background: var(--complete-number-bgColor);
    }
    .step-complete .step-number {
        color: var(--complete-number-textColor);
    }
    .step-item + .step-item.step-complete .step-inner:before {
        background: var(--complete-number-bgColor);
    }
    .step-complete .step-label {
        background: var(--complete-label-bgColor);
        color: var(--complete-label-textColor);
    }
    .step-label .e-check:before {
        content: '\e7ff' !important;
    }
    .e-check {
        display: none;
        position: relative;
        top: 1px;
        margin-left: 3px;
        font-size: 16px;
    }
    .step-complete .step-label .e-check {
        display: inherit;
    }
</style>
